import { Download, NavigateBefore, NavigateNext } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import ActionButton from "components/Buttons/ActionButton";
import TimelinePlayerContext from "features/TimelinePlayer/context/TimelinePlayerContext";
import useCreateDownload from "hooks/useCreateDownload";
import { useContext } from "react";
import colors from "theme/base/colors";
import TimelineItem from "types/TimelineItem";
import useShareDownloadAll from "../hooks/useShareDownloadAll";
import {
  createDownloadFilename,
  isDownloadableItem,
} from "../utils/shareDownloadUtils";

interface Props {
  timelineItems: TimelineItem[];
}

export default function ShareTimelineControls({ timelineItems }: Props) {
  const {
    currentTimelineItem,
    setSeekToAbsolutePosition,
    timelineItemIndex,
    timelinePositions,
  } = useContext(TimelinePlayerContext);

  const startKeys = Array.from(timelinePositions.keys());
  const isFirstVideo = timelineItemIndex <= 0;
  const isLastVideo = timelineItemIndex >= startKeys.length - 1;

  const {
    completedCount,
    downloadAll,
    errorCount,
    hasDownloadable,
    isDownloading: isDownloadingAll,
    totalCount,
  } = useShareDownloadAll({ timelineItems });

  const currentItemIsDownloadable =
    currentTimelineItem != null && isDownloadableItem(currentTimelineItem);

  const currentDownload = useCreateDownload(
    currentTimelineItem?.signedSourceUrl,
    currentTimelineItem
      ? createDownloadFilename(currentTimelineItem, timelineItemIndex)
      : "video.mp4",
  );

  function goToPreviousVideo() {
    if (isFirstVideo) return;
    setSeekToAbsolutePosition(startKeys[timelineItemIndex - 1]);
  }

  function goToNextVideo() {
    if (isLastVideo) return;
    setSeekToAbsolutePosition(startKeys[timelineItemIndex + 1]);
  }

  function getDownloadCurrentLabel() {
    if (currentDownload.isDownloading) {
      return "Downloading...";
    }
    return "Download current";
  }

  function getDownloadAllLabel() {
    if (isDownloadingAll) {
      return `Downloading (${completedCount}/${totalCount})`;
    }
    if (completedCount > 0 && completedCount >= totalCount) {
      return errorCount > 0 ? "Finished with errors" : "Downloaded";
    }
    return "Download all";
  }

  const downloadButtonSx = {
    backgroundColor: colors.primary.main,
    "&:hover": {
      backgroundColor: colors.primary.light,
    },
  };

  return (
    <Stack
      spacing={1.5}
      sx={{
        alignItems: "center",
        backgroundColor: "#eee",
        flexShrink: 0,
        p: "1rem",
        width: "100%",
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <IconButton
          aria-label="Previous video"
          disabled={isFirstVideo}
          onClick={goToPreviousVideo}
          size="large"
        >
          <NavigateBefore />
        </IconButton>
        <Typography variant="body2">
          Video {timelineItemIndex + 1} of {Math.max(startKeys.length, 1)}
        </Typography>
        <IconButton
          aria-label="Next video"
          disabled={isLastVideo}
          onClick={goToNextVideo}
          size="large"
        >
          <NavigateNext />
        </IconButton>
      </Stack>

      {(currentItemIsDownloadable || hasDownloadable) && (
        <Stack direction="row" spacing={1}>
          {currentItemIsDownloadable && (
            <ActionButton
              disabled={isDownloadingAll || currentDownload.isDownloading}
              onClick={currentDownload.onDownload}
              startIcon={<Download />}
              sx={downloadButtonSx}
            >
              {getDownloadCurrentLabel()}
            </ActionButton>
          )}
          {hasDownloadable && (
            <ActionButton
              disabled={isDownloadingAll || currentDownload.isDownloading}
              onClick={downloadAll}
              startIcon={<Download />}
              sx={downloadButtonSx}
            >
              {getDownloadAllLabel()}
            </ActionButton>
          )}
        </Stack>
      )}
    </Stack>
  );
}
